import { useEffect, useState } from "react";
import { api, PRIORITIES } from "@/lib/api";
import { useStatuses } from "@/lib/useStatuses";
import { useAuth } from "@/context/AuthContext";
import { PeriodPicker, defaultPeriod } from "@/components/PeriodPicker";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend } from "recharts";
import { ClipboardList, CheckCircle2, Clock, Timer, AlertTriangle, Hourglass, Package, Loader } from "lucide-react";

function KpiCard({ title, value, icon: Icon, accent, testId, suffix }) {
  return (
    <Card className="relative overflow-hidden border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow" data-testid={testId}>
      <div className="h-1 w-full" style={{ background: accent }} />
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{title}</p>
            <p className="text-2xl font-extrabold text-slate-900 dark:text-white mt-1.5 tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>{value}<span className="text-sm font-semibold text-slate-400 ml-1">{suffix}</span></p>
          </div>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}15` }}><Icon className="w-5 h-5" style={{ color: accent }} strokeWidth={2.3} /></div>
        </div>
      </div>
    </Card>
  );
}

function FSel({ value, onChange, items, label, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 text-xs" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white dark:bg-slate-900">
        <SelectItem value="all">{label}</SelectItem>
        {items.map((s) => <SelectItem key={s.id || s} value={s.id || s}>{s.name || s}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function Dashboard() {
  const { hasPerm, user } = useAuth();
  const isAtendente = user?.role === "ATENDENTE";
  const isRestrictedViewer = isAtendente || user?.role === "CONSULTA";
  const { active: statusList } = useStatuses();
  const [m, setM] = useState(null);
  const [period, setPeriod] = useState(defaultPeriod());
  const [f, setF] = useState({ queue_id: "all", assigned_to: "all", supervisor_id: "all", status: "all", priority: "all", os_type_id: "all" });
  const [cat, setCat] = useState({ queues: [], users: [], types: [] });

  useEffect(() => {
    Promise.all([api.get("/queues"), api.get("/users"), api.get("/os-types")]).then(([q, u, t]) => setCat({ queues: q.data, users: u.data, types: t.data }));
  }, []);

  useEffect(() => {
    const params = { date_from: period.from, date_to: period.to, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "all")) };
    api.get("/dashboard/metrics", { params }).then((r) => setM(r.data));
  }, [period, f]);

  const supervisors = cat.users.filter((u) => u.role === "SUPERVISOR" || u.role === "TENANT_ADMIN");

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Indicadores respeitam o período e os filtros selecionados.</p>
      </div>

      <Card className="p-4 border-slate-200 dark:border-slate-800 space-y-3">
        <PeriodPicker value={period} onApply={setPeriod} />
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
          <FSel value={f.queue_id} onChange={(v) => setF({ ...f, queue_id: v })} items={cat.queues} label="Todas as filas" testId="dash-queue-filter" />
          {!isRestrictedViewer && <FSel value={f.assigned_to} onChange={(v) => setF({ ...f, assigned_to: v })} items={cat.users} label="Todos os usuários" testId="dash-user-filter" />}
          {!isRestrictedViewer && <FSel value={f.supervisor_id} onChange={(v) => setF({ ...f, supervisor_id: v })} items={supervisors} label="Todos supervisores" testId="dash-supervisor-filter" />}
          <FSel value={f.status} onChange={(v) => setF({ ...f, status: v })} items={statusList.map((s) => s.name)} label="Todos os status" testId="dash-status-filter" />
          <FSel value={f.priority} onChange={(v) => setF({ ...f, priority: v })} items={PRIORITIES} label="Todas prioridades" testId="dash-priority-filter" />
          <FSel value={f.os_type_id} onChange={(v) => setF({ ...f, os_type_id: v })} items={cat.types} label="Todos os tipos" testId="dash-type-filter" />
        </div>
      </Card>

      {!m ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse">{[...Array(8)].map((_, i) => <div key={i} className="h-24 bg-slate-200 dark:bg-slate-800 rounded-lg" />)}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
            <KpiCard title="OS Abertas (período)" value={m.abertas} icon={ClipboardList} accent="#F59E0B" testId="kpi-abertas" />
            <KpiCard title="OS Finalizadas (período)" value={m.finalizadas} icon={CheckCircle2} accent="#10B981" testId="kpi-finalizadas" />
            <KpiCard title="Em Atendimento (atual)" value={m.em_atendimento} icon={Loader} accent="#3B82F6" testId="kpi-em-atendimento" />
            <KpiCard title="OS Atrasadas" value={m.atrasadas} icon={AlertTriangle} accent="#EF4444" testId="kpi-atrasadas" />
            <KpiCard title="Pendentes (sem atendimento)" value={m.pendentes} icon={Hourglass} accent="#F59E0B" testId="kpi-pendentes" />
            <KpiCard title="Aguardando (intermediários)" value={(m.aguardando || []).reduce((a, x) => a + x.count, 0)} icon={Package} accent="#A855F7" testId="kpi-aguardando" />
            <KpiCard title="Tempo médio atendimento" value={m.tempo_medio_atendimento_h} suffix="h" icon={Timer} accent="#06B6D4" testId="kpi-tma" />
            <KpiCard title="Tempo médio resolução" value={m.tempo_medio_resolucao_h} suffix="h" icon={Clock} accent="#6366F1" testId="kpi-tmr" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="p-6 lg:col-span-2 border-slate-200 dark:border-slate-800">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Abertas × Finalizadas por dia</h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={m.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8 }} />
                  <Legend />
                  <Line type="monotone" dataKey="abertas" name="Abertas" stroke="#F59E0B" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="finalizadas" name="Finalizadas" stroke="#10B981" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-6 border-slate-200 dark:border-slate-800">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Por status</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart><Pie data={m.by_status.filter((s) => s.count)} dataKey="count" nameKey="status" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {m.by_status.filter((s) => s.count).map((e, i) => <Cell key={i} fill={(m.status_colors?.[e.status] || "#64748B")} />)}</Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-2">
                {m.by_status.map((s) => (
                  <div key={s.status} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: (m.status_colors?.[s.status] || "#64748B") }} /><span className="text-slate-600">{s.status}</span></div>
                    <span className="font-semibold">{s.count}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[["OS por fila", m.by_queue, "queue", "#10B981"], ["OS por usuário", m.by_user, "user", "#3B82F6"], ["OS por tipo", m.by_type, "type", "#A855F7"]].map(([title, data, key, color]) => (
              <Card key={key} className="p-6 border-slate-200 dark:border-slate-800">
                <h3 className="text-base font-bold text-slate-900 dark:text-white mb-4">{title}</h3>
                {data.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">Sem dados no período.</p> : (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data} layout="vertical" margin={{ left: 10 }}>
                      <XAxis type="number" hide allowDecimals={false} />
                      <YAxis type="category" dataKey={key} width={100} stroke="#94a3b8" fontSize={11} />
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8 }} />
                      <Bar dataKey="count" fill={color} radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
